import { Link } from "@tanstack/react-router";
import {
  ArrowDown,
  ArrowUp,
  CalendarCheck,
  CalendarOff,
  ChevronLeft,
  ChevronRight,
  Download,
  PlugZap,
} from "lucide-react";
import { useMemo, useState } from "react";
import { useSetBreadcrumbs } from "@/components/shared/breadcrumbs";
import { EmptyState } from "@/components/shared/empty-state";
import { ErrorState } from "@/components/shared/error-state";
import { PageHeader } from "@/components/shared/page-header";
import { TabBar, type TabItem } from "@/components/shared/tab-bar";
import { type SortState, useTableSort } from "@/components/shared/use-table-sort";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Skeleton } from "@/components/ui/skeleton";
import { SimpleTooltip } from "@/components/ui/tooltip";
import { usePtoReport } from "@/hooks/use-pto";
import { type CsvValue, downloadCsv } from "@/lib/csv";
import { pageOriginSearch } from "@/lib/detail-origin";
import {
  calendarWeeks,
  distinctDates,
  formatDays,
  formatHours,
  formatMonth,
  formatPtoDay,
  groupByAttribute,
  groupByMonth,
  groupByPerson,
  monthOf,
  PTO_TYPE_LABELS,
  PTO_TYPE_TONE,
  PTO_TYPES,
  type PtoEntry,
  type PtoPerson,
  type PtoRequest,
  type PtoType,
  todayKey,
  totalHours,
  totalsByType,
} from "@/lib/pto";
import { cn } from "@/lib/utils";

type View = "calendar" | "by-month" | "by-person" | "upcoming" | "by-team" | "pending";

/**
 * Pending sits last, after the views of leave that is actually booked. It is a
 * different question — "what needs a decision" rather than "who is off" — and
 * the count rides in its label so the queue can be noticed without opening it.
 *
 * **Absent entirely, not empty, for a viewer who may not see it** (`canView`,
 * false for everyone outside the admin tier since 2026-09-22). A tab reading
 * "Pending approval (0)" would tell a colleague nothing is waiting, which is a
 * claim this page has no business making to them.
 */
export function viewsFor(pendingCount: number | null, canView: boolean): TabItem[] {
  return [
    { key: "calendar", label: "Calendar" },
    { key: "by-month", label: "By month" },
    { key: "by-person", label: "By person" },
    { key: "upcoming", label: "Upcoming" },
    { key: "by-team", label: "By team" },
    ...(canView
      ? [
          {
            key: "pending",
            label:
              pendingCount === null ? "Pending approval" : `Pending approval (${pendingCount})`,
          },
        ]
      : []),
  ];
}

/** A full working day. Anything else is annotated so part days stand out. */
const FULL_DAY_HOURS = 8;

/**
 * Days over hours, both on the page. Days is what HR reasons in; hours is the
 * figure NOS actually stores and the one that settles a half-day. Neither is
 * behind a hover — you cannot scan a column of numbers you have to point at.
 */
function Figure({ hours, onClick }: { hours: number; onClick?: () => void }) {
  if (hours === 0) return <span className="text-muted-foreground">—</span>;

  const body = (
    <span className="inline-flex flex-col items-end leading-tight">
      <span className="font-medium tabular-nums">{formatDays(hours)}d</span>
      <span className="text-[0.6875rem] tabular-nums text-muted-foreground">
        {formatHours(hours)}
      </span>
    </span>
  );

  // A zero cell is not clickable — there is nothing behind it to show.
  if (!onClick) return body;
  return (
    <button
      type="button"
      onClick={onClick}
      className="rounded px-1 py-0.5 text-right transition-colors hover:bg-primary/10 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary"
    >
      {body}
    </button>
  );
}

/** A column header that toggles sort, with the direction arrow when active. */
function SortTh({
  label,
  sortKey,
  sort,
  onSort,
  align = "right",
  className,
}: {
  label: string;
  sortKey: string;
  sort: SortState;
  onSort: (key: string) => void;
  align?: "left" | "right";
  className?: string;
}) {
  const active = sort.key === sortKey;
  return (
    <th
      scope="col"
      aria-sort={active ? (sort.dir === "asc" ? "ascending" : "descending") : "none"}
      className={cn("px-3 py-2 font-medium text-muted-foreground", className)}
    >
      <button
        type="button"
        onClick={() => onSort(sortKey)}
        className={cn(
          "inline-flex w-full items-center gap-1 transition-colors hover:text-foreground",
          align === "right" ? "justify-end" : "justify-start",
          active && "text-foreground",
        )}
      >
        {label}
        {active &&
          (sort.dir === "asc" ? <ArrowUp className="size-3" /> : <ArrowDown className="size-3" />)}
      </button>
    </th>
  );
}

/**
 * Orders rows by a named column. Every table here sorts the same way: a
 * `values` map from sort key to a comparable, so adding a column means adding
 * one entry rather than another comparator.
 */
function sortRows<T>(
  rows: T[],
  sort: SortState,
  values: (row: T) => Record<string, string | number>,
): T[] {
  return [...rows].sort((a, b) => {
    const av = values(a)[sort.key];
    const bv = values(b)[sort.key];
    if (av === undefined || bv === undefined) return 0;
    const cmp =
      typeof av === "number" && typeof bv === "number"
        ? av - bv
        : String(av).localeCompare(String(bv));
    return sort.dir === "asc" ? cmp : -cmp;
  });
}

export function PtoReportPage() {
  useSetBreadcrumbs([{ label: "People & Organization" }, { label: "PTO Report" }]);

  const currentYear = new Date().getFullYear();
  const [year, setYear] = useState(currentYear);
  const [view, setView] = useState<View>("calendar");
  const [hidden, setHidden] = useState<Set<PtoType>>(new Set());
  // What the user clicked into: a title plus the exact entries behind that
  // figure. Held here rather than per-view so every table opens the same panel.
  const [drill, setDrill] = useState<{ title: string; entries: PtoEntry[] } | null>(null);

  const { data, isLoading, isError, error, refetch } = usePtoReport(
    `${year}-01-01`,
    `${year}-12-31`,
  );

  // Type filtering happens here, once, so every view below sees the same set.
  const entries = useMemo(
    () => (data?.entries ?? []).filter((e) => !hidden.has(e.type)),
    [data, hidden],
  );
  // Hiding a type removes its COLUMN, not just its numbers — a column of
  // dashes is worse than no column, and the totals have to add up to what is
  // on screen.
  const types = useMemo(() => PTO_TYPES.filter((t) => !hidden.has(t)), [hidden]);
  // The type filter is page-level, so it applies to pending requests too — a
  // filter that silently skipped one tab would be worse than no filter. `null`
  // (the pending read failed) is passed straight through so the view can say so
  // rather than render an empty queue.
  const pendingRequests = useMemo(
    () => data?.pendingRequests?.filter((r) => !hidden.has(r.type)) ?? null,
    [data, hidden],
  );
  // The pending tab can vanish under the viewer — it is absent for anyone
  // outside the admin tier, and `data` arrives after the first render — so fall
  // back rather than leave them on a tab that is no longer there.
  const activeView: View = view === "pending" && data?.canViewPending !== true ? "calendar" : view;

  function toggleType(type: PtoType) {
    setHidden((prev) => {
      const next = new Set(prev);
      if (next.has(type)) next.delete(type);
      else next.add(type);
      return next;
    });
  }

  return (
    <div className="space-y-6">
      <PageHeader
        eyebrow="People & Organization"
        title="PTO Report"
        description="Company-wide leave, read live from NOS."
        actions={<YearPicker year={year} onChange={setYear} currentYear={currentYear} />}
      />

      {isLoading && <PtoSkeleton />}

      {isError && (
        <ErrorState
          message={error instanceof Error ? error.message : "Could not load the PTO report"}
          onRetry={() => {
            void refetch();
          }}
        />
      )}

      {data && !data.connected && <NotConnectedState />}

      {data?.connected && (
        <>
          {year === currentYear && <OutToday entries={data.entries} />}

          <TypeFilter hidden={hidden} onToggle={toggleType} totals={totalsByType(data.entries)} />

          <TabBar
            tabs={viewsFor(
              pendingRequests === null ? null : pendingRequests.length,
              data.canViewPending,
            )}
            value={activeView}
            onChange={(k) => setView(k as View)}
          />

          {activeView === "pending" ? (
            <PendingView requests={pendingRequests} year={year} />
          ) : entries.length === 0 ? (
            <EmptyState
              icon={CalendarOff}
              title="No leave to show"
              description={
                hidden.size > 0
                  ? "Every leave type is filtered out. Turn one back on above."
                  : `Nobody has booked leave in ${year}.`
              }
            />
          ) : (
            <>
              {activeView === "calendar" && <CalendarView entries={entries} year={year} />}
              {activeView === "by-month" && (
                <ByMonthView entries={entries} types={types} onDrill={setDrill} year={year} />
              )}
              {activeView === "by-person" && (
                <ByPersonView
                  entries={entries}
                  people={data.people}
                  types={types}
                  onDrill={setDrill}
                  year={year}
                />
              )}
              {activeView === "upcoming" && (
                <UpcomingView entries={entries} people={data.people} year={year} />
              )}
              {activeView === "by-team" && (
                <ByTeamView
                  entries={entries}
                  people={data.people}
                  types={types}
                  onDrill={setDrill}
                  year={year}
                />
              )}
            </>
          )}
        </>
      )}

      <DrilldownDialog drill={drill} onClose={() => setDrill(null)} />
    </div>
  );
}

/** What a figure hands up when clicked. */
type Drill = { title: string; entries: PtoEntry[] };

/**
 * The people behind one figure. Every aggregate on the report is clickable,
 * and they all land here: who made up that number, how many days each, how
 * many hours, and which dates.
 */
function DrilldownDialog({ drill, onClose }: { drill: Drill | null; onClose: () => void }) {
  const rows = drill
    ? groupByPerson(drill.entries).map((g) => ({
        personId: g.personId,
        name: g.entries[0]?.personName ?? "Unknown",
        hours: totalHours(g.entries),
        dates: distinctDates(g.entries),
      }))
    : [];

  return (
    <Dialog open={drill !== null} onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="max-h-[80vh] overflow-y-auto sm:max-w-3xl">
        <DialogHeader>
          <DialogTitle>{drill?.title ?? ""}</DialogTitle>
          <DialogDescription>
            {rows.length} {rows.length === 1 ? "person" : "people"} ·{" "}
            {formatDays(totalHours(drill?.entries ?? []))} days ·{" "}
            {formatHours(totalHours(drill?.entries ?? []))}
          </DialogDescription>
        </DialogHeader>

        <div className="flex justify-end">
          <CsvButton
            filename={`pto-${(drill?.title ?? "drilldown")
              .toLowerCase()
              .replace(/[^a-z0-9]+/g, "-")
              .replace(/^-|-$/g, "")}`}
            rows={[
              ["Person", "Days", "Hours", "Dates"],
              ...rows.map((r) => [r.name, r.hours / 8, r.hours, r.dates.join("; ")]),
            ]}
          />
        </div>

        <table className="w-full border-collapse text-sm">
          <thead>
            <tr className="border-b text-xs">
              <th scope="col" className="px-2 py-2 text-left font-medium text-muted-foreground">
                Person
              </th>
              <th scope="col" className="px-2 py-2 text-right font-medium text-muted-foreground">
                Days
              </th>
              <th scope="col" className="px-2 py-2 text-right font-medium text-muted-foreground">
                Hours
              </th>
              <th scope="col" className="px-2 py-2 text-left font-medium text-muted-foreground">
                Dates
              </th>
            </tr>
          </thead>
          <tbody>
            {rows.map((row) => (
              <tr key={row.personId} className="border-b last:border-b-0 align-top">
                <td className="px-2 py-2 font-medium">{row.name}</td>
                <td className="px-2 py-2 text-right tabular-nums">{formatDays(row.hours)}</td>
                <td className="px-2 py-2 text-right tabular-nums text-muted-foreground">
                  {formatHours(row.hours)}
                </td>
                <td className="px-2 py-2">
                  {/* Every date, not a count: the question behind a drill-in is
                      usually "when", and a count answers a different one. */}
                  <div className="flex flex-wrap gap-1">
                    {row.dates.map((d) => (
                      <span key={d} className="rounded bg-muted px-1.5 py-0.5 text-xs">
                        {formatPtoDay(d)}
                      </span>
                    ))}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </DialogContent>
    </Dialog>
  );
}

/**
 * Who is off right now. Deliberately reads the UNFILTERED entries: "who is out
 * today" is an operational question with one correct answer, and someone on a
 * public holiday is still out whether or not holidays are hidden from the
 * analysis views below. Only rendered for the current year, since a past year
 * has no today to report on.
 */
function OutToday({ entries }: { entries: PtoEntry[] }) {
  const today = todayKey();
  const out = entries
    .filter((e) => e.date === today)
    .sort((a, b) => a.personName.localeCompare(b.personName));

  return (
    <div className="rounded-xl border border-primary/25 bg-primary/5 p-4">
      <div className="mb-2 flex flex-wrap items-baseline justify-between gap-2">
        <h3 className="text-sm font-semibold">
          Out today{" "}
          <span className="font-normal text-muted-foreground">· {formatPtoDay(today)}</span>
        </h3>
        <span className="text-xs text-muted-foreground">
          {out.length === 0 ? "Nobody" : `${out.length} ${out.length === 1 ? "person" : "people"}`}
        </span>
      </div>

      {out.length === 0 ? (
        <p className="text-sm text-muted-foreground">Everyone is in today.</p>
      ) : (
        <div className="flex flex-wrap gap-1.5">
          {out.map((e) => (
            <span
              key={`${e.personId}-${e.type}`}
              className={cn(
                "inline-flex items-center gap-1.5 rounded-md px-2 py-1 text-xs",
                PTO_TYPE_TONE[e.type],
              )}
            >
              <span className="font-medium">{e.personName}</span>
              <span className="opacity-75">
                {PTO_TYPE_LABELS[e.type]}
                {/* A part day is the thing a reader would otherwise get wrong,
                    so it is spelled out; a full day needs no annotation. */}
                {e.hours !== FULL_DAY_HOURS && ` · ${formatHours(e.hours)}`}
              </span>
            </span>
          ))}
        </div>
      )}
    </div>
  );
}

function PtoSkeleton() {
  return (
    <div className="space-y-3">
      <Skeleton className="h-10 w-full" />
      <Skeleton className="h-96 w-full" />
    </div>
  );
}

function NotConnectedState() {
  return (
    <EmptyState
      icon={PlugZap}
      title="Not connected to NOS"
      description="Leave is read live from the Nymbl Gateway. This environment doesn't have gateway access configured."
    />
  );
}

function YearPicker({
  year,
  onChange,
  currentYear,
}: {
  year: number;
  onChange: (y: number) => void;
  currentYear: number;
}) {
  // NOS holds leave from 2023 onward; there is nothing to page back to before
  // that and nothing booked beyond the current year.
  const canGoBack = year > 2023;
  const canGoForward = year < currentYear;
  return (
    <div className="inline-flex items-center gap-1 rounded-lg border p-0.5">
      <button
        type="button"
        aria-label="Previous year"
        disabled={!canGoBack}
        onClick={() => onChange(year - 1)}
        className="rounded-md p-1.5 text-muted-foreground transition-colors hover:text-foreground disabled:opacity-40"
      >
        <ChevronLeft className="size-4" />
      </button>
      <span className="min-w-[3rem] text-center text-sm font-semibold tabular-nums">{year}</span>
      <button
        type="button"
        aria-label="Next year"
        disabled={!canGoForward}
        onClick={() => onChange(year + 1)}
        className="rounded-md p-1.5 text-muted-foreground transition-colors hover:text-foreground disabled:opacity-40"
      >
        <ChevronRight className="size-4" />
      </button>
    </div>
  );
}

/**
 * Type toggles. Holidays are on by default but are the largest category, so the
 * count beside each label makes it obvious what turning one off removes.
 */
function TypeFilter({
  hidden,
  onToggle,
  totals,
}: {
  hidden: Set<PtoType>;
  onToggle: (t: PtoType) => void;
  totals: Record<PtoType, number>;
}) {
  return (
    <div className="flex flex-wrap items-center gap-2">
      {PTO_TYPES.map((type) => {
        const off = hidden.has(type);
        return (
          <button
            key={type}
            type="button"
            aria-pressed={!off}
            onClick={() => onToggle(type)}
            className={cn(
              "inline-flex items-center gap-1.5 rounded-full border px-3 py-1 text-xs font-medium transition-colors",
              off ? "text-muted-foreground opacity-60" : PTO_TYPE_TONE[type],
            )}
          >
            {type}
            <span className="tabular-nums opacity-80">{formatDays(totals[type])}d</span>
          </button>
        );
      })}
      {hidden.size > 0 && (
        <span className="text-xs text-muted-foreground">
          {hidden.size} type{hidden.size === 1 ? "" : "s"} hidden
        </span>
      )}
    </div>
  );
}

/** Who is off on each day of a month, as a calendar grid. */
function CalendarView({ entries, year }: { entries: PtoEntry[]; year: number }) {
  const today = todayKey();
  // Open on the current month when looking at the current year, otherwise
  // January — landing on "today" is only meaningful if today is in range.
  const [month, setMonth] = useState(() =>
    today.startsWith(String(year)) ? monthOf(today) : `${year}-01`,
  );

  const byDate = useMemo(() => {
    const map = new Map<string, PtoEntry[]>();
    for (const e of entries) {
      const list = map.get(e.date) ?? [];
      list.push(e);
      map.set(e.date, list);
    }
    return map;
  }, [entries]);

  const weeks = calendarWeeks(month);
  const monthIndex = Number(month.slice(5, 7));

  function step(by: number) {
    const next = monthIndex + by;
    if (next < 1 || next > 12) return;
    setMonth(`${year}-${String(next).padStart(2, "0")}`);
  }

  return (
    <div className="rounded-xl border">
      <div className="flex items-center justify-between border-b px-4 py-2">
        <h3 className="text-sm font-semibold">{formatMonth(month)}</h3>
        <div className="inline-flex items-center gap-2">
          <CsvButton
            filename={`pto-calendar-${month}`}
            rows={[
              ["Date", "Person", "Type", "Days", "Hours"],
              ...entries
                .filter((e) => monthOf(e.date) === month)
                .sort(
                  (a, b) =>
                    a.date.localeCompare(b.date) || a.personName.localeCompare(b.personName),
                )
                .map((e) => [e.date, e.personName, e.type, e.hours / 8, e.hours]),
            ]}
          />
          <button
            type="button"
            aria-label="Previous month"
            disabled={monthIndex <= 1}
            onClick={() => step(-1)}
            className="rounded-md p-1.5 text-muted-foreground transition-colors hover:text-foreground disabled:opacity-40"
          >
            <ChevronLeft className="size-4" />
          </button>
          <button
            type="button"
            aria-label="Next month"
            disabled={monthIndex >= 12}
            onClick={() => step(1)}
            className="rounded-md p-1.5 text-muted-foreground transition-colors hover:text-foreground disabled:opacity-40"
          >
            <ChevronRight className="size-4" />
          </button>
        </div>
      </div>

      <div className="grid grid-cols-7 border-b bg-muted/40 text-xs font-medium text-muted-foreground">
        {["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"].map((d) => (
          <div key={d} className="px-2 py-1.5 text-center">
            {d}
          </div>
        ))}
      </div>

      <div className="grid grid-cols-7">
        {weeks.flat().map((date, i) => (
          <CalendarCell
            key={date ?? `pad-${i}`}
            date={date}
            entries={date ? (byDate.get(date) ?? []) : []}
            isToday={date === today}
          />
        ))}
      </div>
    </div>
  );
}

/** How many names a day cell lists before collapsing the rest into a count. */
const NAMES_PER_DAY = 4;

function CalendarCell({
  date,
  entries,
  isToday,
}: {
  date: string | null;
  entries: PtoEntry[];
  isToday: boolean;
}) {
  if (!date) return <div className="min-h-24 border-r border-b bg-muted/20 last:border-r-0" />;

  const day = Number(date.slice(8, 10));
  const shown = entries.slice(0, NAMES_PER_DAY);
  const extra = entries.length - shown.length;

  return (
    <div
      className={cn("min-h-24 border-r border-b p-1.5 last:border-r-0", isToday && "bg-primary/5")}
    >
      <div
        className={cn(
          "mb-1 text-xs font-medium tabular-nums",
          isToday ? "text-primary" : "text-muted-foreground",
        )}
      >
        {day}
      </div>
      <div className="space-y-0.5">
        {shown.map((e) => (
          <div
            key={`${e.personId}-${e.type}`}
            className={cn("truncate rounded px-1 py-0.5 text-2xs", PTO_TYPE_TONE[e.type])}
            title={`${e.personName} — ${e.type} (${formatHours(e.hours)})`}
          >
            {e.personName}
            {e.hours !== FULL_DAY_HOURS && (
              <span className="opacity-75"> · {formatHours(e.hours)}</span>
            )}
          </div>
        ))}
        {extra > 0 && (
          <SimpleTooltip
            content={
              <div className="space-y-0.5">
                {entries.slice(NAMES_PER_DAY).map((e) => (
                  <div key={`${e.personId}-${e.type}`}>
                    {e.personName} — {PTO_TYPE_LABELS[e.type]}
                  </div>
                ))}
              </div>
            }
          >
            <div className="cursor-default px-1 text-2xs font-medium text-muted-foreground">
              +{extra} more
            </div>
          </SimpleTooltip>
        )}
      </div>
    </div>
  );
}

/** Leave per month, split by type. */
function ByMonthView({
  entries,
  types,
  onDrill,
  year,
}: {
  entries: PtoEntry[];
  types: PtoType[];
  onDrill: (d: Drill) => void;
  year: number;
}) {
  const { sort, toggleSort } = useTableSort("month");
  const grand = totalsByType(entries);

  const rows = groupByMonth(entries).map((g) => ({
    month: g.month,
    entries: g.entries,
    totals: totalsByType(g.entries),
    total: totalHours(g.entries),
  }));
  const sorted = sortRows(rows, sort, (r) => ({
    month: r.month,
    total: r.total,
    ...Object.fromEntries(types.map((t) => [t, r.totals[t]])),
  }));

  const csvRows: CsvValue[][] = [
    [
      "Month",
      ...types.flatMap((t) => [`${t} (days)`, `${t} (hours)`]),
      "Total (days)",
      "Total (hours)",
    ],
    ...sorted.map((r) => [
      formatMonth(r.month),
      ...types.flatMap((t) => [r.totals[t] / 8, r.totals[t]]),
      r.total / 8,
      r.total,
    ]),
  ];

  return (
    <TableShell
      csv={{ filename: `pto-by-month-${year}`, rows: csvRows }}
      head={
        <tr className="border-b bg-muted/40 text-xs">
          <SortTh label="Month" sortKey="month" sort={sort} onSort={toggleSort} align="left" />
          {types.map((t) => (
            <SortTh
              key={t}
              label={PTO_TYPE_LABELS[t]}
              sortKey={t}
              sort={sort}
              onSort={toggleSort}
            />
          ))}
          <SortTh label="Total" sortKey="total" sort={sort} onSort={toggleSort} />
        </tr>
      }
      foot={
        <tr className="border-t bg-muted/30 font-semibold">
          <th scope="row" className="px-4 py-2 text-left">
            All months
          </th>
          {types.map((t) => (
            <td key={t} className="px-3 py-2 text-right">
              <Figure
                hours={grand[t]}
                onClick={() =>
                  onDrill({
                    title: `${t} — all months`,
                    entries: entries.filter((e) => e.type === t),
                  })
                }
              />
            </td>
          ))}
          <td className="px-4 py-2 text-right">
            <Figure
              hours={totalHours(entries)}
              onClick={() => onDrill({ title: "All leave", entries })}
            />
          </td>
        </tr>
      }
    >
      {sorted.map((row) => (
        <tr key={row.month} className="border-b last:border-b-0 hover:bg-muted/30">
          <th scope="row" className="px-4 py-2 text-left font-medium">
            {formatMonth(row.month)}
          </th>
          {types.map((t) => (
            <td key={t} className="px-3 py-2 text-right">
              <Figure
                hours={row.totals[t]}
                onClick={() =>
                  onDrill({
                    title: `${t} — ${formatMonth(row.month)}`,
                    entries: row.entries.filter((e) => e.type === t),
                  })
                }
              />
            </td>
          ))}
          <td className="px-4 py-2 text-right font-medium">
            <Figure
              hours={row.total}
              onClick={() =>
                onDrill({ title: `All leave — ${formatMonth(row.month)}`, entries: row.entries })
              }
            />
          </td>
        </tr>
      ))}
    </TableShell>
  );
}

/** Leave per person, split by type. */
function ByPersonView({
  entries,
  people,
  types,
  onDrill,
  year,
}: {
  entries: PtoEntry[];
  people: PtoPerson[];
  types: PtoType[];
  onDrill: (d: Drill) => void;
  year: number;
}) {
  const { sort, toggleSort } = useTableSort("total", "desc");
  const byId = new Map(people.map((p) => [p.personId, p]));

  const rows = groupByPerson(entries).map((g) => ({
    personId: g.personId,
    person: byId.get(g.personId),
    name: byId.get(g.personId)?.name ?? g.entries[0]?.personName ?? "Unknown",
    entries: g.entries,
    totals: totalsByType(g.entries),
    total: totalHours(g.entries),
    daysOff: distinctDates(g.entries).length,
  }));
  const sorted = sortRows(rows, sort, (r) => ({
    person: r.name,
    daysOff: r.daysOff,
    total: r.total,
    ...Object.fromEntries(types.map((t) => [t, r.totals[t]])),
  }));

  const csvRows: CsvValue[][] = [
    [
      "Person",
      "In Enable",
      "Days off",
      ...types.flatMap((t) => [`${t} (days)`, `${t} (hours)`]),
      "Total (days)",
      "Total (hours)",
    ],
    ...sorted.map((r) => [
      r.name,
      r.person?.inEnable ? "yes" : "no",
      r.daysOff,
      ...types.flatMap((t) => [r.totals[t] / 8, r.totals[t]]),
      r.total / 8,
      r.total,
    ]),
  ];

  return (
    <TableShell
      csv={{ filename: `pto-by-person-${year}`, rows: csvRows }}
      head={
        <tr className="border-b bg-muted/40 text-xs">
          <SortTh label="Person" sortKey="person" sort={sort} onSort={toggleSort} align="left" />
          <SortTh label="Days off" sortKey="daysOff" sort={sort} onSort={toggleSort} />
          {types.map((t) => (
            <SortTh
              key={t}
              label={PTO_TYPE_LABELS[t]}
              sortKey={t}
              sort={sort}
              onSort={toggleSort}
            />
          ))}
          <SortTh label="Total" sortKey="total" sort={sort} onSort={toggleSort} />
        </tr>
      }
    >
      {sorted.map((row) => (
        <tr key={row.personId} className="border-b last:border-b-0 hover:bg-muted/30">
          <th scope="row" className="px-4 py-2 text-left font-medium">
            <PersonCell person={row.person} fallback={row.name} />
          </th>
          <td className="px-3 py-2 text-right tabular-nums text-muted-foreground">{row.daysOff}</td>
          {types.map((t) => (
            <td key={t} className="px-3 py-2 text-right">
              <Figure
                hours={row.totals[t]}
                onClick={() =>
                  onDrill({
                    title: `${t} — ${row.name}`,
                    entries: row.entries.filter((e) => e.type === t),
                  })
                }
              />
            </td>
          ))}
          <td className="px-4 py-2 text-right font-medium">
            <Figure
              hours={row.total}
              onClick={() => onDrill({ title: `All leave — ${row.name}`, entries: row.entries })}
            />
          </td>
        </tr>
      ))}
    </TableShell>
  );
}

/**
 * Only people Enable has synced can be linked to a profile. The rest are named
 * from NOS and shown as plain text rather than as a link that would 404.
 */
/**
 * Links a person to their profile where Enable knows them.
 *
 * Takes the structural subset rather than `PtoPerson` so a pending REQUEST can
 * use it too: `people` is derived from approved entries, so someone whose only
 * leave this year is an unapproved request is not in that list and would
 * otherwise lose their link.
 */
function PersonCell({
  person,
  fallback,
}: {
  person: { personId: string; name: string; inEnable: boolean } | undefined;
  fallback: string;
}) {
  if (!person) return <>{fallback}</>;
  if (!person.inEnable) {
    return (
      <SimpleTooltip content="Not synced into Enable yet">
        <span className="text-muted-foreground">{person.name}</span>
      </SimpleTooltip>
    );
  }
  return (
    <Link
      to="/dashboard/persons/$personId"
      params={{ personId: person.personId }}
      search={pageOriginSearch("pto")}
      className="hover:underline"
    >
      {person.name}
    </Link>
  );
}

/** Everything booked from today forward, soonest first. */
function UpcomingView({
  entries,
  people,
  year,
}: {
  entries: PtoEntry[];
  people: PtoPerson[];
  year: number;
}) {
  const today = todayKey();
  const { sort, toggleSort } = useTableSort("date");
  const byId = new Map(people.map((p) => [p.personId, p]));
  const upcoming = entries
    .filter((e) => e.date >= today)
    .sort((a, b) => a.date.localeCompare(b.date));

  if (upcoming.length === 0) {
    return (
      <EmptyState
        icon={CalendarOff}
        title="Nothing booked ahead"
        description={
          year < new Date().getFullYear()
            ? `${year} is in the past — switch to the current year to see upcoming leave.`
            : "Nobody has leave booked from today onward."
        }
      />
    );
  }

  // One row per day, so a week off reads as a block rather than five rows.
  const days = new Map<string, PtoEntry[]>();
  for (const e of upcoming) {
    const list = days.get(e.date) ?? [];
    list.push(e);
    days.set(e.date, list);
  }
  const sorted = sortRows(
    [...days.entries()].map(([date, dayEntries]) => ({ date, entries: dayEntries })),
    sort,
    (r) => ({ date: r.date, people: r.entries.length }),
  );

  const csvRows: CsvValue[][] = [
    ["Date", "People", "Who"],
    ...sorted.map((r) => [
      r.date,
      r.entries.length,
      r.entries
        .map((e) => `${byId.get(e.personId)?.name ?? e.personName} (${e.type}, ${e.hours}h)`)
        .join("; "),
    ]),
  ];

  return (
    <TableShell
      csv={{ filename: `pto-upcoming-${year}`, rows: csvRows }}
      head={
        <tr className="border-b bg-muted/40 text-xs">
          <SortTh label="Date" sortKey="date" sort={sort} onSort={toggleSort} align="left" />
          <th scope="col" className="px-3 py-2 text-left font-medium text-muted-foreground">
            Off
          </th>
          <SortTh label="People" sortKey="people" sort={sort} onSort={toggleSort} />
        </tr>
      }
    >
      {sorted.map(({ date, entries: dayEntries }) => (
        <tr key={date} className="border-b last:border-b-0 hover:bg-muted/30">
          <th scope="row" className="whitespace-nowrap px-4 py-2 text-left font-medium">
            {formatPtoDay(date)}
            {date === today && <span className="ml-2 text-xs text-primary">today</span>}
          </th>
          <td className="px-3 py-2">
            <div className="flex flex-wrap gap-1">
              {dayEntries.map((e) => (
                <span
                  key={`${e.personId}-${e.type}`}
                  className={cn("rounded px-1.5 py-0.5 text-xs", PTO_TYPE_TONE[e.type])}
                  title={`${e.type} (${formatHours(e.hours)})`}
                >
                  {byId.get(e.personId)?.name ?? e.personName}
                </span>
              ))}
            </div>
          </td>
          <td className="px-4 py-2 text-right tabular-nums font-medium">{dayEntries.length}</td>
        </tr>
      ))}
    </TableShell>
  );
}

/** Leave grouped by department, then role team. */
function ByTeamView({
  entries,
  people,
  types,
  onDrill,
  year,
}: {
  entries: PtoEntry[];
  people: PtoPerson[];
  types: PtoType[];
  onDrill: (d: Drill) => void;
  year: number;
}) {
  const [attribute, setAttribute] = useState<"departmentName" | "roleTeamName">("departmentName");
  const { sort, toggleSort } = useTableSort("total", "desc");
  const noun = attribute === "departmentName" ? "department" : "role team";

  const groups = groupByAttribute(entries, people, attribute, {
    notSet: `No ${noun} set`,
    notSynced: "Not synced into Enable",
  });
  const attributed = groups.filter((g) => g.attributed);

  const rows = groups.map((g) => ({
    ...g,
    totals: totalsByType(g.entries),
    total: totalHours(g.entries),
  }));
  const sorted = sortRows(rows, sort, (r) => ({
    label: r.label,
    people: r.headcount,
    total: r.total,
    ...Object.fromEntries(types.map((t) => [t, r.totals[t]])),
  }))
    // Whatever the sort, the two fallback buckets stay at the bottom — they are
    // not real groups, and letting a large one head the table would read as if
    // "no department set" were a department.
    .sort((a, b) => (a.attributed === b.attributed ? 0 : a.attributed ? -1 : 1));

  const csvRows: CsvValue[][] = [
    [
      attribute === "departmentName" ? "Department" : "Role team",
      "People",
      ...types.flatMap((t) => [`${t} (days)`, `${t} (hours)`]),
      "Total (days)",
      "Total (hours)",
    ],
    ...sorted.map((r) => [
      r.label,
      r.headcount,
      ...types.flatMap((t) => [r.totals[t] / 8, r.totals[t]]),
      r.total / 8,
      r.total,
    ]),
  ];

  return (
    <div className="space-y-3">
      <div className="inline-flex items-center rounded-lg border p-0.5">
        {(
          [
            ["departmentName", "Department"],
            ["roleTeamName", "Role team"],
          ] as const
        ).map(([key, label]) => (
          <button
            key={key}
            type="button"
            aria-pressed={attribute === key}
            onClick={() => setAttribute(key)}
            className={cn(
              "rounded-md px-2.5 py-1 text-xs font-medium transition-colors",
              attribute === key
                ? "bg-muted text-foreground"
                : "text-muted-foreground hover:text-foreground",
            )}
          >
            {label}
          </button>
        ))}
      </div>

      {attributed.length === 0 && (
        <div className="rounded-lg border bg-muted/40 p-3 text-sm text-muted-foreground">
          <span className="font-medium text-foreground">Nothing is grouped yet.</span> Leave comes
          from NOS, but department and role team are set in Enable, and nobody who took leave has
          one assigned. Fill those in on the person records and this view fills in with them.
        </div>
      )}

      <TableShell
        csv={{ filename: `pto-by-${noun.replace(" ", "-")}-${year}`, rows: csvRows }}
        head={
          <tr className="border-b bg-muted/40 text-xs">
            <SortTh
              label={attribute === "departmentName" ? "Department" : "Role team"}
              sortKey="label"
              sort={sort}
              onSort={toggleSort}
              align="left"
            />
            <SortTh label="People" sortKey="people" sort={sort} onSort={toggleSort} />
            {types.map((t) => (
              <SortTh
                key={t}
                label={PTO_TYPE_LABELS[t]}
                sortKey={t}
                sort={sort}
                onSort={toggleSort}
              />
            ))}
            <SortTh label="Total" sortKey="total" sort={sort} onSort={toggleSort} />
          </tr>
        }
      >
        {sorted.map((group) => (
          <tr
            key={group.label}
            className={cn(
              "border-b last:border-b-0 hover:bg-muted/30",
              !group.attributed && "text-muted-foreground",
            )}
          >
            <th scope="row" className="px-4 py-2 text-left font-medium">
              {group.label}
            </th>
            <td className="px-3 py-2 text-right tabular-nums">{group.headcount}</td>
            {types.map((t) => (
              <td key={t} className="px-3 py-2 text-right">
                <Figure
                  hours={group.totals[t]}
                  onClick={() =>
                    onDrill({
                      title: `${t} — ${group.label}`,
                      entries: group.entries.filter((e) => e.type === t),
                    })
                  }
                />
              </td>
            ))}
            <td className="px-4 py-2 text-right font-medium">
              <Figure
                hours={group.total}
                onClick={() =>
                  onDrill({ title: `All leave — ${group.label}`, entries: group.entries })
                }
              />
            </td>
          </tr>
        ))}
      </TableShell>
    </div>
  );
}

/**
 * Downloads what is currently on screen. Deliberately built from the same rows
 * the table renders, so the file matches the filter and sort the user set
 * rather than re-deriving a "canonical" export they didn't ask for. Every
 * figure is exported as BOTH days and hours, so nobody has to divide by eight
 * in a spreadsheet to get back to what NOS stored.
 */
/**
 * Leave awaiting a decision.
 *
 * Deliberately a table of REQUESTS rather than of days, and deliberately absent
 * from the calendar and every total on the other tabs. Two reasons, both
 * properties of the data rather than presentation choices:
 *
 * - NOS stores a request as a date RANGE plus hours-per-day and only expands it
 *   into real booked days — skipping weekends and company holidays — when it is
 *   approved. Placing a pending request on the calendar would mean guessing
 *   which days it will occupy.
 * - These days are not approved, so counting them in "days taken" would
 *   overstate leave on figures HR reads as settled.
 */
function PendingView({ requests, year }: { requests: PtoRequest[] | null; year: number }) {
  const { sort, toggleSort } = useTableSort("start");
  const today = todayKey();

  // Null means the approved report loaded but this read failed. Saying so beats
  // an empty table, which would read as "nothing is waiting for you".
  if (requests === null) {
    return (
      <ErrorState message="Approved leave loaded, but the pending-requests feed did not answer. The queue below may not be empty — try again shortly." />
    );
  }

  if (requests.length === 0) {
    return (
      <EmptyState
        icon={CalendarCheck}
        title="Nothing awaiting approval"
        description={`Every leave request dated ${year} has been decided.`}
      />
    );
  }

  const sorted = sortRows(requests, sort, (r) => ({
    start: r.startDate,
    person: r.personName,
    type: r.type,
    days: r.days,
  }));

  const csvRows: CsvValue[][] = [
    [
      "Person",
      "Type",
      "From",
      "To",
      "Days (est.)",
      "Hours per day",
      "Approver",
      "Requested",
      "Reason",
    ],
    ...sorted.map((r) => [
      r.personName,
      r.type,
      r.startDate,
      r.endDate,
      r.days,
      r.hoursPerDay,
      r.approverName ?? "",
      r.requestedOn ?? "",
      r.description ?? "",
    ]),
  ];

  return (
    <TableShell
      csv={{ filename: `pto-pending-${year}`, rows: csvRows }}
      note={
        <>
          These days are <strong>not</strong> approved, so they are excluded from every figure on
          the other tabs and from the calendar. Day counts are an estimate: weekends are excluded,
          company holidays cannot be, so a request spanning one reads a day high.
        </>
      }
      head={
        <tr className="border-b bg-muted/40 text-xs">
          <SortTh label="Person" sortKey="person" sort={sort} onSort={toggleSort} align="left" />
          <SortTh label="Type" sortKey="type" sort={sort} onSort={toggleSort} align="left" />
          <SortTh label="Dates" sortKey="start" sort={sort} onSort={toggleSort} align="left" />
          <SortTh label="Days" sortKey="days" sort={sort} onSort={toggleSort} />
          <th scope="col" className="px-3 py-2 text-left font-medium text-muted-foreground">
            Approver
          </th>
          <th scope="col" className="px-3 py-2 text-left font-medium text-muted-foreground">
            Reason
          </th>
        </tr>
      }
    >
      {sorted.map((r) => (
        <tr key={r.id} className="border-b last:border-b-0 hover:bg-muted/30">
          <th scope="row" className="whitespace-nowrap px-4 py-2 text-left font-medium">
            <PersonCell
              person={{ personId: r.personId, name: r.personName, inEnable: r.inEnable }}
              fallback={r.personName}
            />
          </th>
          <td className="px-3 py-2">
            <span className={cn("rounded px-1.5 py-0.5 text-xs", PTO_TYPE_TONE[r.type])}>
              {PTO_TYPE_LABELS[r.type]}
            </span>
          </td>
          <td className="whitespace-nowrap px-3 py-2">
            <DateRange startDate={r.startDate} endDate={r.endDate} today={today} />
          </td>
          <td className="px-4 py-2 text-right tabular-nums">
            <SimpleTooltip
              content={`${formatHours(r.hoursPerDay)} per day. Weekdays in the range; company holidays are not deducted.`}
            >
              <span>{r.days === 0 ? "—" : `~${r.days}`}</span>
            </SimpleTooltip>
          </td>
          <td className="px-3 py-2 text-muted-foreground">
            {r.approverName ?? <span className="italic">Unassigned</span>}
          </td>
          <td className="max-w-xs px-3 py-2 text-muted-foreground">
            {r.description === null ? (
              <span className="text-muted-foreground/60">—</span>
            ) : (
              <span className="line-clamp-2 break-words" title={r.description}>
                {r.description}
              </span>
            )}
          </td>
        </tr>
      ))}
    </TableShell>
  );
}

/** `Thu 27 Aug` for a single day, `Thu 27 – Fri 28 Aug` for a span. */
function DateRange({
  startDate,
  endDate,
  today,
}: {
  startDate: string;
  endDate: string;
  today: string;
}) {
  const past = endDate < today;
  return (
    <span className={past ? "text-muted-foreground" : undefined}>
      {formatPtoDay(startDate)}
      {endDate !== startDate && <> – {formatPtoDay(endDate)}</>}
      {past && <span className="ml-2 text-xs">already passed</span>}
    </span>
  );
}

function CsvButton({
  filename,
  rows,
  className,
}: {
  filename: string;
  rows: CsvValue[][];
  className?: string;
}) {
  return (
    <button
      type="button"
      onClick={() => downloadCsv(filename, rows)}
      className={cn(
        "inline-flex items-center gap-1.5 rounded-md border px-2.5 py-1 text-xs font-medium transition-colors hover:bg-muted",
        className,
      )}
    >
      <Download className="size-3.5" />
      Download CSV
    </button>
  );
}

/** Shared table chrome: bordered card, horizontal scroll, days-are-hover note. */
function TableShell({
  head,
  children,
  foot,
  csv,
  note,
}: {
  head: React.ReactNode;
  children: React.ReactNode;
  foot?: React.ReactNode;
  /**
   * Exports the rows this table shows — same filter, same sort, same columns.
   * The totals row is deliberately left out: a summary line in the middle of a
   * CSV fights every sort and pivot the file is opened to do.
   */
  csv?: { filename: string; rows: CsvValue[][] };
  /**
   * Overrides the default footer note. The default explains the days-over-hours
   * figures and the type columns, neither of which the pending-requests table
   * has — leaving it there would describe a table that isn't on screen.
   */
  note?: React.ReactNode;
}) {
  return (
    <div className="rounded-xl border">
      {csv && (
        <div className="flex justify-end border-b px-3 py-2">
          <CsvButton filename={csv.filename} rows={csv.rows} />
        </div>
      )}
      <div className="overflow-x-auto">
        <table className="w-full border-collapse text-sm">
          <thead>{head}</thead>
          <tbody>{children}</tbody>
          {foot && <tfoot>{foot}</tfoot>}
        </table>
      </div>
      <p className="border-t px-4 py-2 text-xs text-muted-foreground">
        {note ?? (
          <>
            Each figure shows days over hours. Hiding a type above removes its column, so the totals
            always add up to what is on screen.
          </>
        )}
      </p>
    </div>
  );
}
